import React, { useState, FC } from 'react';

import {
  Box,
  Container,
  Divider,
  Grid,
  makeStyles,
  Select,
  MenuItem,
  FormControl
} from '@material-ui/core';
import AppsIcon from '@material-ui/icons/Apps';
import ListIcon from '@material-ui/icons/List';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';

import { first, isEmpty } from 'lodash';

import { OrderingItem, OrderingCategory, OnlineOrder } from 'kiolyn-api';

import { LayoutVariant } from '../../Core/CommonTypes';

import CategoryList from './CategoryList/CategoryList';
import ItemGrid from './ItemGrid/ItemGrid';
import ItemList from './ItemList/ItemList';

import calculateCategorySchedules from '../../../domain/usecases/CalculateCategorySchedules';

const useStyles = makeStyles(theme => ({
  categoryTypes: {
    marginBottom: theme.spacing(2)
  },
  mobileCategoryTypes: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  wrap_categories: {
    flexWrap: 'nowrap',
    whiteSpace: 'pre-wrap'
  },
  categories: {
    flexWrap: 'nowrap',
    height: theme.spacing(7),
    paddingTop: 4
  },
  itemGrid: {
    marginTop: theme.spacing(1)
  }
}));

interface OrderingInteractionProps {
  showCategoryTypes?: boolean;
  allCategoryTypes?: string[];
  categories?: OrderingCategory[];
  selectItem?: (category: OrderingCategory, item: OrderingItem) => void;
  variant?: LayoutVariant;
  acceptOrdering?: boolean;
  defaultView?: boolean;
  order?: OnlineOrder;
  delayTime?: number;
  deliveryTime?: number;
}

const OrderingInteraction: FC<OrderingInteractionProps> = ({
  showCategoryTypes,
  categories,
  selectItem,
  variant,
  acceptOrdering,
  defaultView,
  order,
  delayTime,
  deliveryTime,
  allCategoryTypes
}: OrderingInteractionProps) => {
  const classes = useStyles();
  let defaultViewString = 'list';
  if (defaultView === true) {
    defaultViewString = 'grid';
  }
  const originalCategories = categories ?? [];
  let categoryTypes: string[] =
    showCategoryTypes && allCategoryTypes?.length ? allCategoryTypes : [];

  const initialCategoryType = first(categoryTypes);
  const initialCategories = initialCategoryType
    ? originalCategories.filter(cat =>
        cat.category_types?.includes(initialCategoryType)
      )
    : originalCategories;
  const initialCategory = first(initialCategories);
  const initialItems = initialCategory?.items;

  const [state, setState] = useState({
    viewType: defaultViewString,
    categoryType: initialCategoryType,
    categories: initialCategories,
    category: initialCategory,
    items: initialItems
  });

  const handleCategoryTypeChange = (categoryType: string) => {
    if (isEmpty(categoryType)) {
      return;
    }
    const categories = originalCategories.filter(cat =>
      cat.category_types?.includes(categoryType)
    );
    const category = first(categories);
    setState({
      ...state,
      categoryType,
      categories,
      category,
      items: category?.items
    });
  };

  const handleCategoryChange = (category: OrderingCategory) => {
    setState({
      ...state,
      category,
      items: category.items
    });
  };

  const internalSelectItem = (item: OrderingItem) => {
    if (selectItem && state.category) {
      selectItem(state.category, item);
    }
  };

  const categoryTypeSelectionView = (
    <Grid
      container
      direction="column"
      alignItems="center"
      className={
        variant === LayoutVariant.mobile
          ? classes.mobileCategoryTypes
          : classes.categoryTypes
      }
    >
      <CategoryTypeSelection
        variant={variant}
        value={state.categoryType}
        types={categoryTypes}
        onChange={handleCategoryTypeChange}
      />
    </Grid>
  );

  const unavailableMessage = calculateCategorySchedules(
    state.category,
    order,
    delayTime,
    deliveryTime
  );

  return variant === LayoutVariant.mobile ? (
    <Box>
      {categoryTypes.length > 1 && categoryTypeSelectionView}
      <CategoryList
        category={state.category}
        categories={state.categories}
        onCategoryChange={handleCategoryChange}
      />
      <Divider></Divider>
      <ItemList
        items={state.items}
        getUnavailableMessage={() => unavailableMessage}
        onSelect={internalSelectItem}
        acceptOrdering={acceptOrdering}
      />
    </Box>
  ) : (
    <Container>
      {categoryTypes.length > 1 && categoryTypeSelectionView}
      <Grid
        container
        direction="row"
        justify="space-between"
        className={
          variant === LayoutVariant.kiosk
            ? classes.wrap_categories
            : classes.categories
        }
      >
        <CategoryList
          category={state.category}
          categories={state.categories}
          onCategoryChange={handleCategoryChange}
          variant={variant}
        />
        <ViewTypeSelection
          viewType={state.viewType}
          onChange={viewType => setState({ ...state, viewType })}
        />
      </Grid>
      <Divider></Divider>
      {state.viewType === 'grid' ? (
        <Box className={classes.itemGrid}>
          <ItemGrid
            items={state.items}
            getUnavailableMessage={() => unavailableMessage}
            onSelect={internalSelectItem}
            acceptOrdering={acceptOrdering}
          />
        </Box>
      ) : (
        <ItemList
          items={state.items}
          getUnavailableMessage={() => unavailableMessage}
          onSelect={internalSelectItem}
          acceptOrdering={acceptOrdering}
        />
      )}
    </Container>
  );
};

interface ViewTypeSelectionProps {
  viewType: string;
  onChange: (view: string) => void;
}

const ViewTypeSelection: FC<ViewTypeSelectionProps> = ({
  viewType,
  onChange
}: ViewTypeSelectionProps) => {
  return (
    <ToggleButtonGroup
      value={viewType}
      exclusive
      onChange={(_, value) => onChange(value)}
      aria-label="pickup delivery dine-in"
    >
      <ToggleButton value="list" aria-label="display list">
        <ListIcon></ListIcon>
      </ToggleButton>
      <ToggleButton value="grid" aria-label="display grid">
        <AppsIcon></AppsIcon>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

const useCategoryTypeSelectionStyles = makeStyles(theme => ({
  toggleSelected: {
    color: `${theme.palette.primary.main} !important`,
    background: `transparent !important`
  },
  select: {
    minWidth: 280
  }
}));

interface CategoryTypeSelectionProps {
  variant?: LayoutVariant;
  value?: string;
  types: string[];
  onChange: (value: string) => void;
}

const CategoryTypeSelection: FC<CategoryTypeSelectionProps> = ({
  variant,
  value,
  types,
  onChange
}) => {
  const classes = useCategoryTypeSelectionStyles();
  return variant === LayoutVariant.mobile ? (
    <FormControl variant="outlined">
      <Select
        value={value}
        onChange={e => onChange(e.target.value as string)}
        name="category_type"
        className={classes.select}
      >
        {types.map(type => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, value) => onChange(value)}
      aria-label="select food group"
    >
      {types.map(type => (
        <ToggleButton
          key={type}
          value={type}
          aria-label={`select ${type}`}
          classes={{
            selected: classes.toggleSelected
          }}
        >
          {type}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export default OrderingInteraction;
